// @ts-strict-ignore
import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { LMDashboardPanelBaseComponent } from '@dashboard/lm-dashboard-panel-base.component';
import { LandmarkPanel } from '@form/lm-form.model';
import { LMFormatterService } from '@list/formatters/lm-formatter.service';
import { LMListCardService } from '@list/services/lm-list-card.service';
import { LMModel } from '@form/model/lm-model';
import { LMWorker } from 'app/types/lm-worker';
import { forkJoin, Observable } from 'rxjs';
import { LMPanelTransactionEvent } from '@form/services/lm-form-panel.service';
import { SohoHomePageComponent } from 'ids-enterprise-ng';
import { LMDashboardPanelListComponent } from './lm-dashboard-panel-list.component';
import { LMRefreshRequest } from '@app/components';

export class LMDashboardPanelsSaveWorker extends LMWorker {
  private dashboardPanels: LMDashboardPanelBaseComponent[];

  constructor(dashboardPanels: LMDashboardPanelBaseComponent[]) {
    super();

    this.dashboardPanels = dashboardPanels;
  }

  protected has(i: number): boolean {
    return (i < this.dashboardPanels.length);
  }

  protected get(i: number): LMDashboardPanelBaseComponent {
    return this.dashboardPanels[ i ];
  }

  protected run(i: number): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const dashboardPanel = this.dashboardPanels[ i ];
      if (dashboardPanel && dashboardPanel.isModified()) {
        dashboardPanel.save(observer);
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }
}

@Component({
  selector: 'lm-dashboard-view',
  standalone: false,
  templateUrl: './lm-dashboard-view.component.html',
  styleUrls: ['./lm-dashboard-view.component.scss'],
  providers: [ LMFormatterService, LMListCardService ]
})
export class LMDashboardViewComponent implements AfterViewChecked {

  /**
   * The formUrlInfo of the main form that uses this dashboard view.
   * This will nnot be used for lists with dashboard/detail sections.
   * Can likely be removed once the new spec-url refactor is complete
   * and solely in use.
   */
  @Input() formUrlInfo: LMFormUrlInfo;

  /**
   * The number of columns the card should layout into (given available space).
   */
  @Input() columns: 3 | 4 = 3;

  @Input() set dashboardPanels(panels: Array<LandmarkPanel>) { // used by list and form for detail sections and form tabs.
    this.m_dashboardPanels = panels;
    this.refreshPanels();
  }
  @Input() model: LMModel; // used by composite forms when displaying panels as dashboard panes
  @Input() specFields: LMSpecFields; // used by composite forms when displaying panels as dashboard panes
  @Input() placeHolder: ViewContainerRef; // used by composite forms when displaying panels as dashboard panes
  @Input() set fromDataView(fromDataView: LMDataView) { // optional if used in a page panel
    this.m_fromDataView = fromDataView;
    this.refreshPanels();
  }
  @Input() set messageRuleView(messageRuleView: LMDataView) {
    this.m_messageRuleView = messageRuleView;
  }

  @Input() set panelVisibilityUpdateTimeStamp(timeStamp: number) {
    if (timeStamp !== this.m_panelVisibilityUpdateTimeStamp) {
      this.m_panelVisibilityUpdateTimeStamp = timeStamp;
      this.refreshPanels();
    }
  }

  @Input() displayedOnPagePanel: boolean;
  @Input() isFlyoutContent = false;
  @Input() refreshAfterInit = false;

  @Output() contentModified = new EventEmitter<any>();
  @Output() uiLinkEvent = new EventEmitter<any>();
  @Output() fieldEvent = new EventEmitter<LMFieldEvent>();
  @Output() dashboardContentDisplayed = new EventEmitter<any>();
  @Output() sendContextMessageEvent = new EventEmitter<any>();
  @Output() panelTransactionEvent = new EventEmitter<LMPanelTransactionEvent>();
  @Output() drillAroundEvent = new EventEmitter<LMDrillAroundEvent>();

  @ViewChildren(LMDashboardPanelBaseComponent) lmDashboardPanelComponentList: QueryList<LMDashboardPanelBaseComponent>;
  @ViewChildren('dashboardPanel') lmDashboardPanels: QueryList<LMDashboardPanelBaseComponent>;
  @ViewChild(SohoHomePageComponent) sohoHomepage: SohoHomePageComponent;

  private m_messageRuleView: LMDataView;
  private m_fromDataView: LMDataView;
  private m_dashboardPanels: Array<LandmarkPanel>;
  private m_panelVisibilityUpdateTimeStamp: number;
  private displayedDashboards = 0;
  private m_refreshPanels = false;

  constructor(private elementRef: ElementRef) {}

  get fromDataView(): LMDataView {
    return this.m_fromDataView;
  }

  get messageRuleView(): LMDataView {
    return this.m_messageRuleView;
  }

  // todo might be nice to update the dashboard panels less often by using either a Pipe or another
  //  method to update then when the fromDataViewChanges.
  get dashboardPanels(): Array<LandmarkPanel> {
    return this.m_dashboardPanels.filter(panel => panel.isVisible && !panel.hidden && panel.isValid);
  }

  ngAfterViewChecked(): void {
    if (this.m_refreshPanels && this.sohoHomepage && this.hasFinishedRendering()) {
      this.m_refreshPanels = false;

      // LMWEB-25290: Only call refreshHomepage when refreshAfterInit is set.
      // In composite form tabs, the IDS homepage widget's own init() handles the
      // initial layout via a 100ms timeout with the correct container width.
      // Calling refreshHomepage from ngAfterViewChecked in that case fires resize()
      // before the container has its stable width (~16px narrower), causing the widget
      // to miscalculate and collapse the height to ~15px.
      // Usages where data (fromDataView) arrives after the IDS widget has already
      // initialized need the refresh to relayout once panels are populated.
      if (this.refreshAfterInit) {
        this.refreshHomepage(false);
      }
    }
  }

  private hasFinishedRendering(): boolean {
    return this.dashboardPanels.length === this.elementRef.nativeElement.getElementsByClassName('widget').length;
  }

  isModified(): boolean {
    return this.lmDashboardPanels.some(dashboardPanel => dashboardPanel.isModified());
  }

  private getDashboardPanelToRefresh(excludedList?: string): LMDashboardPanelBaseComponent[] {
    if (!excludedList) {
      return this.lmDashboardPanels.toArray();
    }

    const dashboardPanels: LMDashboardPanelBaseComponent[] = [];
    this.lmDashboardPanels.forEach(dashBoardComponent => {

      if (dashBoardComponent instanceof LMDashboardPanelListComponent) {
        const dashBoardList = <LMDashboardPanelListComponent>dashBoardComponent;
        if (dashBoardList.lmListComponent.listId !== excludedList) {
          dashboardPanels.push(dashBoardComponent);
        }
      } else {
        dashboardPanels.push(dashBoardComponent);
      }
    });

    return dashboardPanels;
  }

  private includeInRefresh(dashboardComponent: LMDashboardPanelBaseComponent, request?: LMRefreshRequest): boolean {
    if (dashboardComponent instanceof LMDashboardPanelListComponent) {
      const dashBoardList = <LMDashboardPanelListComponent>dashboardComponent;
      return dashBoardList.lmListComponent.listId !== request?.originatingSourceId;
    } else {
      return true;
    }
  }

  refresh(request?: LMRefreshRequest): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const refreshCalls: Observable<boolean>[] = this.lmDashboardPanels.toArray()
        .filter(dashboardComponent => this.includeInRefresh(dashboardComponent, request))
        .map((dashboardComponent: LMDashboardPanelBaseComponent) => dashboardComponent.refresh());
      // console.log('dashboardView to be refresh:' + refreshCalls.length);

      forkJoin(refreshCalls).subscribe(value => {
        // console.log('dashboardView to be forkJoin:');
        observer.next(true);
        observer.complete();
      });
    });
  }

  public refreshPanels() {
    this.m_refreshPanels = true;
  }

  runSaveAction(): Observable<boolean> {
    const dashboardPanels: LMDashboardPanelBaseComponent[] = this.lmDashboardPanels.toArray();

    return new Observable<boolean>(observer => {
      this.runSavePanels(dashboardPanels).subscribe(
        (continueOn: boolean) => {
          observer.next(true);
          observer.complete();
        },
        (err => {
          observer.next(false);
          observer.complete();
        }));
    });
  }

  saveDashboardViewState() {
    this.lmDashboardPanelComponentList.forEach(dashboardComponent => dashboardComponent.saveDashboardPanelState());
  }

  clearDashboardViewState() {
    this.lmDashboardPanelComponentList.forEach(dashboardComponent => dashboardComponent.clearDashboardPanelState());
  }

  onContentModified(event: any) {
    this.contentModified.emit(event);
  }

  onUILinkEvent(event) {
    this.uiLinkEvent.emit(event);
  }

  onFieldEvent(event: LMFieldEvent) {
    this.fieldEvent.emit(event);
  }

  onSendContextMessageEvent(event: any) {
    this.sendContextMessageEvent.emit(event);
  }

  onPanelTransactionEvent(event: LMPanelTransactionEvent) {
    this.panelTransactionEvent.emit(event);
  }

  onDrillAroundEvent(event: LMDrillAroundEvent) {
    this.drillAroundEvent.emit(event);
  }

  onComponentDisplayed() {
    this.displayedDashboards = this.displayedDashboards + 1;
    if (this.displayedDashboards === this.dashboardPanels.length) {
      this.dashboardContentDisplayed.emit();
    }
  }

  runSavePanels(dashboardPanels: LMDashboardPanelBaseComponent[]): Observable<boolean> {
    const saveWorker: LMDashboardPanelsSaveWorker = new LMDashboardPanelsSaveWorker(dashboardPanels);
    return saveWorker.start();
  }

  public refreshHomepage(animate = false) {
    if (this.sohoHomepage) {
      this.sohoHomepage.refresh(animate);
    }
  }

  public getPanelHeight(): number {
    if (this.elementRef) {
      return this.elementRef.nativeElement.offsetHeight;
    }
    return 0;
  }
}
